import { Cloud$Country, Cloud$Market } from '../../types/cloudEnums';
import { Otovo$Locale } from '../../types/otovoweb';
import { Sanity$DocumentType } from '../../types/sanityTypes';

type SanityURLConfigByMarket = {
  type: Sanity$DocumentType;
  localization: string;
  language: Otovo$Locale;
};

type SanityURLConfig = {
  [market in Cloud$Country]: SanityURLConfigByMarket[];
};

// This stub will be replaced with the actual Sanity URL config when we have it
// in Sanity.
const stubbedUrlConfig: SanityURLConfig = {
  at: [{ type: 'productPage', localization: 'produkte', language: 'de-at' }],
  be: [
    { type: 'productPage', localization: 'produits', language: 'fr-be' },
    { type: 'productPage', localization: 'producten', language: 'nl-be' },
  ],
  ch: [
    { type: 'productPage', localization: 'produkte', language: 'de-ch' },
    { type: 'productPage', localization: 'produits', language: 'fr-ch' },
    { type: 'productPage', localization: 'prodotti', language: 'it-ch' },
  ],
  de: [{ type: 'productPage', localization: 'produkte', language: 'de-de' }],
  es: [{ type: 'productPage', localization: 'productos', language: 'es-es' }],
  fr: [{ type: 'productPage', localization: 'produits', language: 'fr-fr' }],
  gb: [{ type: 'productPage', localization: 'products', language: 'en-gb' }],
  it: [
    { type: 'productPage', localization: 'prodotti', language: 'it-it' },
    {
      type: 'packagePage',
      localization: 'kit-fotovoltaici',
      language: 'it-it',
    },
    { type: 'quotePage', localization: 'preventivo', language: 'it-it' },
    { type: 'pricingPage', localization: 'prezzi-garanzia', language: 'it-it' },
  ],
  no: [{ type: 'productPage', localization: 'produkter', language: 'nb-no' }],
  nl: [{ type: 'productPage', localization: 'producten', language: 'nl-nl' }],
  pl: [{ type: 'productPage', localization: 'produkty', language: 'pl-pl' }],
  pt: [{ type: 'productPage', localization: 'productos', language: 'pt-pt' }],
  se: [{ type: 'productPage', localization: 'produkter', language: 'sv-se' }],
};

type PathToPageType = {
  from: string;
  to: Sanity$DocumentType;
};
const PATH_TO_PAGE_TYPE: PathToPageType[] = [
  { from: '/products', to: 'productPage' },
  { from: '/packages', to: 'packagePage' },
  { from: '/get-a-quote', to: 'quotePage' },
  { from: '/pricing', to: 'pricingPage' },
];

type URLConfigByMarket = {
  nextPath: string;
  localizedPath: string;
};

// Returns the mapping between the path in Next.js and the Sanity localization,
// if there is a mapping. The mapping works both ways.
//
// For example, if the nextPath is '/products/batteria', we'll look for a
// localization for the 'productPage' type in Sanity for the given market and
// return:
//
// {
//   nextPath: '/products',
//   localizedPath: '/prodotti'
// }
//
// Since the mapping works both ways, we'll also return that object when the
// nextPath is the localized variant ('/prodotti/batteria').
//
// null is returned if there is no mapping for the given path and market.
export function getURLConfigForPath(
  nextPath: string,
  market: Cloud$Market,
  locale: Otovo$Locale,
): URLConfigByMarket | null {
  const urls = stubbedUrlConfig[market];
  if (!urls) {
    return null;
  }

  function getPageType() {
    // Check if the path is the non-localized variant (for example "/products")
    const pageType = PATH_TO_PAGE_TYPE.find((item) =>
      nextPath.startsWith(item.from),
    );

    if (pageType) {
      return pageType;
    }

    // Check if the path is the localized variant (for example "/produkter")

    const withoutLeadingSlash = nextPath.replace(/^\//, '');

    const sanityURLConfig = urls.find((url) =>
      withoutLeadingSlash.startsWith(url.localization),
    );

    if (!sanityURLConfig) {
      return null;
    }

    return PATH_TO_PAGE_TYPE.find((item) => item.to === sanityURLConfig.type);
  }

  const pageType = getPageType();

  if (!pageType) {
    return null;
  }

  const sanityUrlConfig = urls.find(
    (url) => url.type === pageType.to && url.language === locale,
  );

  if (!sanityUrlConfig) {
    return null;
  }

  return {
    nextPath: pageType.from,
    localizedPath: `/${sanityUrlConfig.localization}`,
  };
}
